import React, { Fragment, useCallback, useMemo } from "react";

import useYardActionEvents from "@hsl/core/hooks/useYardAnalyticsEvents";
import cx from "@hsl/core/utils/cx";
import { type TopLevelDataObj } from "@hsl/fund-explorer/types";

import { type TabConfig } from "../../../config/types";
import useFundData from "../../../hooks/useFundData";
import useGroupedShareClasses from "../../../hooks/useGroupedShareClasses";
import useMounted from "../../../hooks/useMounted";
import { Translate } from "../../../hooks/useTranslation";
import { ControlledCollapsible } from "../../Collapsible";
import { NoData } from "../../FundCard/components/Messages";
import { useFundCardContext } from "../../FundCard/context";
import { type Tab } from "../../Tabs/components/Tab";
import { type Props as FundCardTableProps } from "../FundCardTable";
import Table from "../Table";
import View from "./View";

const GroupedTabView = ({
    shareClasses,
    config,
    tab,
    subTabs,
    footnote,
    spacedTabs,
    groups,
}: FundCardTableProps) => {
    const groupOn = tab.groupOn;
    const { openShareClassGroups, toggleShareClass } = useFundCardContext();
    const groupedShareClasses = useGroupedShareClasses(
        shareClasses,
        groupOn,
        false,
        config.productType === "pmc" ? ["primary"] : [],
    );
    const gShareClasses = groups ?? groupedShareClasses;

    const mounted = useMounted();
    const fundData = useFundData(
        Object.entries(groupedShareClasses)[0]?.[1][0]?.data.parentId ?? "",
    );
    const { priceType } = fundData?.data?.data ?? {};
    const fundDeps = [["priceType", priceType]]
        .filter(([_, val]) => Boolean(val))
        .reduce((acc, [key, val]) => ({ ...acc, [key as string]: val }), {});
    const { expandCollapseEvent, tabEvent } = useYardActionEvents();

    const trackTabChange = (key: string) => {
        tabEvent({
            key: `${tab.label} - ${key}`,
            fund: {
                name: String(fundData?.data?.data.name),
                id: Number(fundData?.data?.id),
                type: String(fundData?.type),
            },
        });
    };

    const scWithData = useCallback(
        (g: string) => {
            return (sc: TopLevelDataObj) => {
                if (!groups) {
                    return true;
                }
                const [groupKey] = g.split("@");
                const groupCols = (tab as TabConfig).columns?.filter(
                    (col) => col.group === groupKey,
                );
                return groupCols?.some(
                    (col) => sc.data[col.prop] && sc.data[col.prop] !== "-",
                );
            };
        },
        [tab, groups],
    );

    const getShareClassData = useCallback(
        (scKey: string) => {
            return gShareClasses[scKey]
                ?.map((x) => (groups ? x : x.data) as TopLevelDataObj)
                ?.filter(scWithData(scKey));
        },
        [groups],
    );

    const hasData = useMemo(
        () =>
            Object.keys(gShareClasses)
                .map((shareClassGroup) => {
                    const shareClassData = getShareClassData(shareClassGroup);
                    return shareClassData && shareClassData.length !== 0;
                })
                .includes(true),
        [getShareClassData],
    );

    const Content = React.useMemo(() => {
        if (Array.isArray(subTabs) && subTabs.length > 0) {
            return subTabs.map((subTab) => <>{renderShareClasses(subTab)}</>);
        } else {
            return <>{renderShareClasses(tab)}</>;
        }

        function renderShareClasses(tab: TabConfig | Tab) {
            return Object.keys(gShareClasses).map((shareClassGroup, i, arr) => {
                const collapsibleKey = `${tab.key}-${shareClassGroup}`;
                const shareClassData = getShareClassData(shareClassGroup);

                if (!shareClassData || shareClassData.length === 0) {
                    return <Fragment key={collapsibleKey} />;
                }

                const columnGroup = groups ? shareClassGroup.split("@")[0] : "";
                const labelText = groups
                    ? shareClassGroup.split("@").pop()
                    : shareClassGroup;
                const label = (
                    <span className="whitespace-pre">
                        {!groupOn && !groups ? (
                            <Translate
                                insert={labelText}
                                children={"{} share classes"}
                            />
                        ) : (
                            labelText
                        )}
                    </span>
                );
                const handleToggle = (_: any, open?: boolean) => {
                    const _fund = fundData
                        ? {
                              name: fundData.data.data.name,
                              id: fundData.data.id,
                              type: fundData.type,
                          }
                        : undefined;
                    toggleShareClass(collapsibleKey);
                    expandCollapseEvent({
                        value: open ? "open" : "close",
                        fund: _fund,
                        group: labelText,
                    });
                };
                const _tab =
                    "columns" in tab && columnGroup
                        ? {
                              ...tab,
                              columns: tab.columns?.filter(
                                  (c) => !c.group || c.group === columnGroup,
                              ),
                              columnsMobile: tab.columnsMobile?.filter(
                                  (c) => !c.group || c.group === columnGroup,
                              ),
                          }
                        : tab;
                const isOpen = openShareClassGroups
                    .concat(openShareClassGroups.map((g) => `${tab.key}-${g}`))
                    .includes(collapsibleKey);
                return (
                    <ControlledCollapsible
                        key={collapsibleKey}
                        label={label}
                        labelClassNames={cx(
                            "border-platinum font-medium w-full flex items-center z-30 relative",
                            i === 0 && "mt-1 sm:mt-0",
                            isOpen && "sm:border-b pb-2",
                        )}
                        className={cx(
                            arr.length - 1 === i
                                ? "mb-1 sm:mb-2"
                                : "mb-2 sm:mb-3",
                        )}
                        withChevron
                        isOpen={isOpen}
                        onClick={handleToggle}
                    >
                        <Table
                            tab={_tab as TabConfig}
                            config={config}
                            data={
                                shareClassData as unknown as TopLevelDataObj[]
                            }
                            fundDeps={fundDeps}
                            containerClassName="-mt-2 sm:mt-0"
                        />
                    </ControlledCollapsible>
                );
            });
        }
    }, [
        subTabs,
        groups,
        groupedShareClasses,
        groupOn,
        openShareClassGroups,
        config,
        fundDeps,
        fundData,
        toggleShareClass,
        expandCollapseEvent,
        tab,
    ]);

    React.useEffect(() => {
        if (
            !mounted &&
            openShareClassGroups.length === 0 &&
            Object.keys(groups ?? groupedShareClasses)?.length > 0
        ) {
            const [groupKey] = Object.keys(groups ?? groupedShareClasses);
            groupKey && toggleShareClass(`${tab.key}-${groupKey}`);
            tab.tabs &&
                tab.tabs.length > 0 &&
                tab.tabs.forEach((subTab) =>
                    toggleShareClass(`${subTab}-${groupKey}`),
                );
        }
    }, [
        groupedShareClasses,
        mounted,
        openShareClassGroups.length,
        toggleShareClass,
        groups,
        tab,
    ]);

    if (!hasData) {
        return <NoData />;
    }

    return (
        <View
            content={Content}
            tabs={subTabs}
            config={config.tabs.find((t) => t.key === tab.key) as TabConfig}
            footnote={footnote}
            spacedTabs={spacedTabs}
            onChange={trackTabChange}
        />
    );
};

export default GroupedTabView;
